import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import ResponsiveList from '../../components/common/ResponsiveList';
import {
  ORG_TYPES,
  ORG_TYPE_LABELS,
  createOrganisation,
  createStation,
  deleteOrganisation,
  deleteStation,
  getOrganisation,
  listOrganisations,
  updateOrganisation,
} from '../../api/organisations';

const BLANK = {
  name: '',
  org_type: 'fire_department',
  contact_info: '',
  notes: '',
  is_active: true,
};

/**
 * The register of outside agencies that turn up to fires: fire departments,
 * private services, government bodies, NGOs.
 *
 * Global, not per-FPA — one brigade assists many FPAs, so this is super-admin
 * territory and lives in the admin hub rather than in FPA settings. Assisting
 * *FPAs* are not managed here at all: they are tenants, and one that does not
 * use the system is a shadow FPA on the FPA areas tab.
 */
const OrganisationList = () => {
  const [organisations, setOrganisations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [editing, setEditing] = useState(null); // null = dialog closed
  const [form, setForm] = useState(BLANK);
  const [stations, setStations] = useState([]);
  const [stationDraft, setStationDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listOrganisations({ page: 1, page_size: 200 });
      setOrganisations(data.organisations || []);
    } catch (err) {
      console.error('Error loading organisations:', err);
      setError('Failed to load organisations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const openCreate = () => {
    setForm(BLANK);
    setStations([]);
    setStationDraft('');
    setEditing({ id: null });
  };

  const openEdit = async (org) => {
    setForm({
      name: org.name || '',
      org_type: org.org_type || 'other',
      contact_info: org.contact_info || '',
      notes: org.notes || '',
      is_active: org.is_active !== false,
    });
    setStationDraft('');
    setEditing(org);
    // Stations only come back on the detail read; the list route leaves them
    // null rather than issuing a query per row.
    try {
      const full = await getOrganisation(org.id);
      setStations(full.stations || []);
    } catch (err) {
      console.error('Error loading stations:', err);
      setStations([]);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      if (editing.id) {
        await updateOrganisation(editing.id, form);
      } else {
        await createOrganisation(form);
      }
      setEditing(null);
      fetch();
    } catch (err) {
      console.error('Error saving organisation:', err);
      setError(err?.response?.data?.detail || 'Failed to save organisation.');
    } finally {
      setSaving(false);
    }
  };

  // Stations are saved immediately against a persisted organisation rather
  // than batched with the form: they are sub-items with their own routes, and
  // there is no id to hang them off until the organisation exists.
  const handleAddStation = async () => {
    const name = stationDraft.trim();
    if (!name || !editing?.id) return;
    try {
      const created = await createStation(editing.id, { name, is_active: true });
      setStations((prev) => [...prev, created]);
      setStationDraft('');
    } catch (err) {
      console.error('Error adding station:', err);
      setError('Failed to add station.');
    }
  };

  const handleRemoveStation = async (station) => {
    try {
      await deleteStation(editing.id, station.id);
      setStations((prev) => prev.filter((s) => s.id !== station.id));
    } catch (err) {
      console.error('Error removing station:', err);
      setError('Failed to remove station.');
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteOrganisation(confirmDelete.id);
      fetch();
    } catch (err) {
      console.error('Error deleting organisation:', err);
      setError('Failed to delete organisation.');
    } finally {
      setConfirmDelete(null);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon fontSize="small" /> Organisations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Outside agencies that can be credited when they assist on a fire.
            Shared across every FPA.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Add organisation
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <ResponsiveList
        items={organisations}
        loading={loading}
        empty="No organisations yet. Add the fire departments and private services that turn out in your area."
        getKey={(o) => o.id}
        columns={[
          {
            key: 'name',
            header: 'Name',
            render: (o) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {o.name}
                {o.is_active === false && (
                  <Chip label="Retired" size="small" variant="outlined" />
                )}
              </Box>
            ),
          },
          {
            key: 'type',
            header: 'Type',
            render: (o) => ORG_TYPE_LABELS[o.org_type] || o.org_type || '-',
          },
          { key: 'contact', header: 'Contact', render: (o) => o.contact_info || '-' },
          {
            key: 'actions',
            header: 'Actions',
            render: (o) => (
              <>
                <Tooltip title="Edit">
                  <IconButton size="small" color="primary" onClick={() => openEdit(o)} sx={{ mr: 1 }}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" color="error" onClick={() => setConfirmDelete(o)}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </>
            ),
          },
        ]}
        mobileCard={(o) => (
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{o.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {ORG_TYPE_LABELS[o.org_type] || o.org_type}
              {o.contact_info ? ` · ${o.contact_info}` : ''}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button size="small" onClick={() => openEdit(o)}>Edit</Button>
              <Button size="small" color="error" onClick={() => setConfirmDelete(o)}>Delete</Button>
            </Stack>
          </Box>
        )}
      />

      {/* Create / edit */}
      <Dialog open={Boolean(editing)} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle>{editing?.id ? 'Edit organisation' : 'Add organisation'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
              required
              helperText="Must be unique — two rows for the same brigade would split its stats."
            />
            <TextField
              select
              label="Type"
              value={form.org_type}
              onChange={(e) => setForm({ ...form, org_type: e.target.value })}
              fullWidth
            >
              {ORG_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Contact"
              value={form.contact_info}
              onChange={(e) => setForm({ ...form, contact_info: e.target.value })}
              fullWidth
              helperText="A switchboard or depot number, not someone's personal mobile."
            />
            <TextField
              label="Notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
              }
              label="Active (selectable on fire reports)"
            />

            {editing?.id && (
              <>
                <Divider />
                <Typography variant="subtitle2">Stations</Typography>
                <Typography variant="caption" color="text.secondary">
                  Optional. Only worth adding for organisations big enough that
                  which depot turned out actually matters.
                </Typography>
                {stations.map((s) => (
                  <Box key={s.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ flexGrow: 1 }}>{s.name}</Typography>
                    <IconButton size="small" color="error" onClick={() => handleRemoveStation(s)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    label="Add a station"
                    size="small"
                    value={stationDraft}
                    onChange={(e) => setStationDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddStation();
                      }
                    }}
                    fullWidth
                  />
                  <Button onClick={handleAddStation} disabled={!stationDraft.trim()}>
                    Add
                  </Button>
                </Box>
              </>
            )}
            {!editing?.id && (
              <Typography variant="caption" color="text.secondary">
                Save the organisation first, then reopen it to add stations.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.name.trim()}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete {confirmDelete?.name}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This removes the organisation and its stations. Fires that already
            credited it keep the name they recorded, but it can never be
            selected again. Retiring it instead (clear “Active”) usually does
            what you want.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrganisationList;
